/**
 * Pending-photo state for the Flux Agent composer. Each photo arrives already shrunk and
 * re-encoded (`lib/fluxAgentPhoto`), shows a local thumbnail at once, and uploads to the media
 * store; ready photos contribute their ids to the next message's `mediaIds`. Removing a ready
 * photo deletes it from the server.
 *
 * @module apps/web/src/components/flux-agent/useFluxAgentPhotos
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fluxAgentApi, type FluxAgentMedia } from '@/lib/fluxAgentApi';

/** One composer photo thumbnail. */
export interface PendingPhoto {
  /** Client-side key, stable across the upload. */
  key: string;
  /** Local `blob:` URL of the prepared photo, revoked when the thumbnail goes away. */
  previewUrl: string;
  /** Upload progress; only `ready` photos are sent. */
  status: 'uploading' | 'ready' | 'error';
  /** Server record once uploaded. */
  media?: FluxAgentMedia;
  /** Why the upload failed, in the server's words. */
  error?: string;
}

/** State and operations returned by {@link useFluxAgentPhotos}. */
export interface FluxAgentPhotos {
  /** Thumbnails in capture order, including failed ones until removed. */
  photos: PendingPhoto[];
  /** True while any photo is still uploading. */
  uploading: boolean;
  /** Ids of uploaded photos, in capture order. */
  readyIds: string[];
  /** True when another photo would exceed the per-message limit. */
  full: boolean;
  /** Add prepared photos and start uploading them; extras past the limit are ignored. */
  add: (blobs: Blob[]) => void;
  /** Drop a thumbnail and delete its uploaded photo (best effort). */
  remove: (key: string) => void;
  /** Forget all thumbnails after a send; the photos now belong to the message. */
  clear: () => void;
}

/** Manage photos for the next message, at most `maxPhotos` of them (4 when the server is older). */
export function useFluxAgentPhotos(maxPhotos = 4): FluxAgentPhotos {
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const counter = useRef(0);
  const activeKeys = useRef(new Set<string>());
  const latest = useRef(photos);
  latest.current = photos;

  const patch = useCallback((key: string, update: Partial<PendingPhoto>) => {
    setPhotos(current => current.map(item => (item.key === key ? { ...item, ...update } : item)));
  }, []);

  const add = useCallback(
    (blobs: Blob[]) => {
      const room = Math.max(0, maxPhotos - activeKeys.current.size);
      const added = blobs.slice(0, room).map(blob => {
        const key = `photo-${++counter.current}`;
        activeKeys.current.add(key);
        void fluxAgentApi.uploadPhoto(blob).then(result => {
          if (!activeKeys.current.has(key)) {
            if (result.ok) void fluxAgentApi.deleteMedia(result.data.media.id);
            return;
          }
          if (result.ok) patch(key, { status: 'ready', media: result.data.media });
          else patch(key, { status: 'error', error: result.message });
        });
        return { key, previewUrl: URL.createObjectURL(blob), status: 'uploading' as const };
      });
      if (added.length) setPhotos(current => [...current, ...added]);
    },
    [maxPhotos, patch]
  );

  const remove = useCallback((key: string) => {
    activeKeys.current.delete(key);
    const target = latest.current.find(item => item.key === key);
    if (target?.media) void fluxAgentApi.deleteMedia(target.media.id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    setPhotos(current => current.filter(item => item.key !== key));
  }, []);

  const clear = useCallback(() => {
    // Previews stay valid briefly so the optimistic message can show them; the stored message
    // then loads the photos from the server.
    activeKeys.current.clear();
    const previews = latest.current.map(item => item.previewUrl);
    window.setTimeout(() => previews.forEach(url => URL.revokeObjectURL(url)), 60_000);
    setPhotos([]);
  }, []);

  useEffect(
    () => () => {
      activeKeys.current.clear();
      latest.current.forEach(item => URL.revokeObjectURL(item.previewUrl));
    },
    []
  );

  return {
    photos,
    uploading: photos.some(item => item.status === 'uploading'),
    readyIds: photos.flatMap(item =>
      item.status === 'ready' && item.media ? [item.media.id] : []
    ),
    full: photos.length >= maxPhotos,
    add,
    remove,
    clear,
  };
}
